import { useEffect, useState } from "react";
import { Loader2, CalendarDays } from "lucide-react";
import { API, authHeaders, fmtDate } from "./adminApi";

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-heading text-lg font-semibold text-foreground mb-3">{title}</h3>
    {children}
  </div>
);

const Empty = ({ text }) => <p className="font-body text-sm text-muted-foreground">{text}</p>;

const expDetails = (e) => {
  const bits = [];
  if (e.event_date) bits.push(`date: ${fmtDate(e.event_date)}`);
  if (e.film) bits.push(`film: ${e.film}`);
  return bits.length ? ` (${bits.join(", ")})` : "";
};

const headCount = (it) => {
  if (it.pricing_option === "per_person") {
    return `${it.qty} guest${it.qty !== 1 ? "s" : ""}${it.children ? ` + ${it.children} kid${it.children !== 1 ? "s" : ""}` : ""}`;
  }
  return it.qty > 1 ? `×${it.qty}` : "";
};

export const BookingsTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/dashboard`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <Empty text="Could not load bookings. Please refresh or sign in again." />;
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div data-testid="admin-bookings-tab" className="space-y-8">
      <Section title="Experience Schedule (upcoming add-ons by day)">
        {(data.experience_schedule || []).length === 0 ? (
          <Empty text="No scheduled experiences coming up. Add-ons with a preferred date will appear here by day." />
        ) : (
          <div className="space-y-3" data-testid="experience-schedule">
            {data.experience_schedule.map((day) => (
              <div key={day.date} data-testid={`schedule-day-${day.date}`} className="rounded-lg border border-primary/25 bg-primary/[0.03] p-4">
                <p className="font-heading text-base font-semibold text-primary flex items-center gap-2 mb-2">
                  <CalendarDays size={16} /> {fmtDate(day.date)}
                  <span className="font-body text-xs text-muted-foreground font-normal">· {day.items.length} item{day.items.length !== 1 ? "s" : ""}</span>
                </p>
                <div className="space-y-1.5">
                  {day.items.map((it, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white border border-border/50 px-3 py-2 font-body text-sm">
                      <span className="font-medium text-foreground">
                        {it.name}{headCount(it) ? ` · ${headCount(it)}` : ""}{it.film ? ` · Film: ${it.film}` : ""}
                      </span>
                      <span className="text-muted-foreground text-xs">{it.guest || "Guest"} · {it.email}{it.phone ? ` · ${it.phone}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Upcoming Arrivals (synced from Guesty)">
        {data.arrivals.length === 0 ? <Empty text="No upcoming arrivals." /> : (
          <div className="space-y-2">
            {data.arrivals.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <span className="font-medium text-foreground">{a.guest_name || "Guest"}</span>
                <span className="text-muted-foreground">{fmtDate(a.check_in)} → {fmtDate(a.check_out)}</span>
                <span className="text-muted-foreground">{a.source || ""}</span>
                <span className="font-semibold text-primary">${a.total ?? "—"}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Website Stay Bookings">
        {data.bookings.length === 0 ? <Empty text="No paid stay bookings yet." /> : (
          <div className="space-y-2">
            {data.bookings.map((b) => (
              <div key={b.id} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{b.first_name} {b.last_name} · {b.listing_title || b.listing_id}</span>
                  <span className="text-muted-foreground">{fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.guests_count} guests</span>
                  <span className="text-muted-foreground">{b.email}</span>
                  <span className="font-semibold text-primary">${b.amount?.toFixed(2)}</span>
                </div>
                {b.addons?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">Add-ons: {b.addons.map((a) => a.name).join(", ")}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Guesty: {b.reservation_status}{b.guesty_reservation_id ? ` · ${b.guesty_reservation_id}` : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Cart Orders (add-ons & bundles)">
        {data.orders.length === 0 ? <Empty text="No cart orders yet." /> : (
          <div className="space-y-2">
            {data.orders.map((o) => (
              <div key={o.id} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{o.first_name} {o.last_name}</span>
                  <span className="text-muted-foreground">{o.email}</span>
                  <span className="font-semibold text-primary">${o.amount?.toFixed(2)}</span>
                </div>
                {o.experiences?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {o.experiences.map((e) => `${e.name} ×${e.qty}${expDetails(e)}`).join("; ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Event Inquiries (weddings, groups, retreats)">
        {(data.event_inquiries || []).length === 0 ? <Empty text="No event inquiries yet." /> : (
          <div className="space-y-2">
            {data.event_inquiries.map((q, i) => (
              <div key={q.id || i} data-testid={`event-inquiry-${q.id || i}`} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{q.name}</span>
                  <span className="text-muted-foreground">{q.email} {q.phone ? `· ${q.phone}` : ""}</span>
                  <span className="text-muted-foreground">{[q.event_type, q.group_size, q.preferred_dates].filter(Boolean).join(" · ")}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(q.created_at)}</span>
                </div>
                {q.message && <p className="text-muted-foreground mt-1">{q.message}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Contact Messages">
        {(data.contacts || []).length === 0 ? <Empty text="No contact messages yet." /> : (
          <div className="space-y-2">
            {data.contacts.map((c, i) => (
              <div key={c.id || i} data-testid={`contact-message-${c.id || i}`} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{c.name}</span>
                  <span className="text-muted-foreground">{c.email} {c.phone ? `· ${c.phone}` : ""}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</span>
                </div>
                {c.message && <p className="text-muted-foreground mt-1">{c.message}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Chat Inquiries">
        {(data.chat_inquiries || []).length === 0 ? <Empty text="No chat messages yet." /> : (
          <div className="space-y-2">
            {data.chat_inquiries.map((c) => (
              <div key={c.id} data-testid={`chat-inquiry-${c.id}`} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{c.name}</span>
                  <span className="text-muted-foreground">{c.email} {c.phone ? `· ${c.phone}` : ""}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(c.created_at)}{c.page ? ` · from ${c.page}` : ""}</span>
                </div>
                <p className="text-muted-foreground mt-1">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Experience Inquiries">
        {data.inquiries.length === 0 ? <Empty text="No inquiries yet." /> : (
          <div className="space-y-2">
            {data.inquiries.map((q, i) => (
              <div key={q.id || i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <span className="font-medium text-foreground">{q.name}</span>
                <span className="text-muted-foreground">{q.email} {q.phone ? `· ${q.phone}` : ""}</span>
                <span className="text-muted-foreground">{(q.experience_ids || []).join(", ")}</span>
                <span className="text-muted-foreground">{q.preferred_date || ""}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};
